import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle, XCircle, Clock, Tag, AlertCircle, AlertTriangle,
} from 'lucide-react';
import { formatBrandMoney, getBrandLocale } from './brandCurrency';
import { getBrandPriceTierThresholds } from './brandCurrency';
import localApiAdapter from '../../core/services/localApiAdapter';
import { sortRecordsNewestFirst } from './listSortUtils';
import { generateEntityReference, getEntityPrefix } from './generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';
import { dispatchCrmApiRefresh, CRM_REFRESH_BY_API } from '../../pages/crm/crmRefreshEvents';

/** CRM entity reference prefixes (settings.configuration.entityPrefixes). */
export const CRM_ENTITY_REF_DEFAULTS = {
  lead: { prefixKey: 'lead', fallback: 'LED', field: 'lead_ref' },
  contact: { prefixKey: 'contact', fallback: 'CON', field: 'contact_ref' },
  opportunity: { prefixKey: 'opportunity', fallback: 'OPP', field: 'opportunity_ref' },
  campaign: { prefixKey: 'campaign', fallback: 'CMP', field: 'campaign_ref' },
  interaction: { prefixKey: 'interaction', fallback: 'INT', field: 'interaction_ref' },
  salesGoal: { prefixKey: 'salesGoal', fallback: 'GOL', field: 'goal_ref' },
};

/** Next CRM entity reference (LED-001, OPP-002, …). */
export function generateCrmEntityReference(settings, prefixKey, fallback, records, refField) {
  const prefix = getEntityPrefix(settings, prefixKey, fallback);
  const field = refField || CRM_ENTITY_REF_DEFAULTS[prefixKey]?.field;
  return generateEntityReference(prefix, records, (r) => (field ? r[field] : r));
}

/** Hook: stable callback to generate the next reference for a CRM entity list. */
export function useCrmEntityReference(prefixKey, fallback, records, refField) {
  const { settings } = useSettings();
  const field = refField || CRM_ENTITY_REF_DEFAULTS[prefixKey]?.field;
  return useCallback(
    () => generateCrmEntityReference(settings, prefixKey, fallback, records, field),
    [settings, prefixKey, fallback, records, field],
  );
}

/**
 * CRM modal tab rules (field count → tab count):
 * ≤8 fields → no tabs | 9–17 → 2 tabs | ≥18 → 2–3 tabs (max 3).
 */
export function getCrmModalTabCount(fieldCount) {
  const n = Number(fieldCount) || 0;
  if (n <= 8) return 0;
  if (n <= 17) return 2;
  return 3;
}

/** Trim tab definitions to the allowed count; returns [] when tabs are not needed. */
export function resolveCrmModalTabs(fieldCount, tabs = []) {
  const allowed = getCrmModalTabCount(fieldCount);
  if (allowed === 0 || !tabs.length) return [];
  return tabs.slice(0, allowed);
}

/** Shared helpers for CRM list pages (Saudi localization). */
export function useCrmPage(namespace) {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.${namespace}.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(dateLocale) : '—');
  const fmtMoney = (n) => formatBrandMoney(n);
  const priceTiers = getBrandPriceTierThresholds();

  return { t, p, dateLocale, fmtDate, fmtMoney, priceTiers };
}

const defaultExtractCrmList = (response) => {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object' && Array.isArray(response.data)) return response.data;
  return [];
};

export function useCrmData(apiPath, extractList) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const extractRef = useRef(extractList || defaultExtractCrmList);
  extractRef.current = extractList || defaultExtractCrmList;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get(apiPath);
      setItems(sortRecordsNewestFirst(extractRef.current(response)));
    } catch (error) {
      console.error(`CRM load error (${apiPath}):`, error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!CRM_REFRESH_BY_API[apiPath]) return undefined;
    const handler = () => { void load(); };
    window.addEventListener(CRM_REFRESH_BY_API[apiPath], handler);
    return () => window.removeEventListener(CRM_REFRESH_BY_API[apiPath], handler);
  }, [apiPath, load]);

  const save = async (data, editingId) => {
    try {
      if (editingId) {
        await localApiAdapter.put(`${apiPath}/${editingId}`, data);
      } else {
        await localApiAdapter.post(apiPath, data);
      }
      dispatchCrmApiRefresh(apiPath);
      await load();
    } catch (error) {
      console.error(`CRM save error (${apiPath}):`, error);
      throw error;
    }
  };

  const remove = async (id) => {
    try {
      await localApiAdapter.delete(`${apiPath}/${id}`);
      dispatchCrmApiRefresh(apiPath);
      await load();
    } catch (error) {
      console.error(`CRM delete error (${apiPath}):`, error);
      throw error;
    }
  };

  return { items, loading, load, save, remove, setItems };
}

/** ROI = ((conversions * avgValue) - spent) / spent * 100 — simplified for campaigns. */
export function calcCampaignRoi(spent, conversions, budget) {
  const s = parseFloat(spent) || 0;
  const c = parseInt(conversions, 10) || 0;
  const b = parseFloat(budget) || 0;
  if (s <= 0) return 0;
  const estimatedReturn = c > 0 ? (b / Math.max(c, 1)) * c : 0;
  return (((estimatedReturn - s) / s) * 100).toFixed(1);
}

/** Classify customer value tier using Saudi SAR thresholds. */
export function getValueTier(amount, priceTiers, p) {
  const v = parseFloat(amount) || 0;
  if (v > priceTiers.highMin) return p('tier_high', { amount: formatBrandMoney(priceTiers.highMin, false) });
  if (v > priceTiers.lowMax) {
    return p('tier_medium', {
      low: formatBrandMoney(priceTiers.lowMax + 1, false),
      high: formatBrandMoney(priceTiers.highMin, false),
    });
  }
  return p('tier_low', { amount: formatBrandMoney(priceTiers.lowMax, false) });
}

export const CRM_STATUS_STYLES = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  paused: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  expired: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  scheduled: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  new: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  contacted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  qualified: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  converted: 'bg-green-500/20 text-green-400 border-green-500/30',
  lost: 'bg-red-500/20 text-red-400 border-red-500/30',
  prospect: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  client: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  training: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
  closed_won: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed_lost: 'bg-red-500/20 text-red-400 border-red-500/30',
  lead: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  proposal: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  negotiation: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const CRM_STATUS_ICONS = {
  active: CheckCircle,
  inactive: XCircle,
  pending: Clock,
  completed: CheckCircle,
  scheduled: Clock,
  cancelled: XCircle,
  draft: Tag,
  paused: AlertTriangle,
  training: AlertCircle,
  suspended: AlertTriangle,
};

/** Add icon/style defaults for QawaemClickableStatusBadge. */
export function enrichStatusOptions(options = []) {
  return options.map((opt) => ({
    ...opt,
    icon: opt.icon || CRM_STATUS_ICONS[opt.value] || Tag,
    style: opt.style || CRM_STATUS_STYLES[opt.value] || CRM_STATUS_STYLES.draft,
  }));
}

export function StatusBadge({ status, label }) {
  const style = CRM_STATUS_STYLES[status] || CRM_STATUS_STYLES.draft;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${style}`}>
      {label || status}
    </span>
  );
}
